/**
 * Copyright (c) 2010-2024 California CAD Solutions, Inc. Licensed subject
 * to an End-User License Agreement included in a separate file named EULA.txt
 * that is distributed with this file.
 */
import { APP_BASE_HREF } from '@angular/common';
import { Inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { AutoLoginConfig } from '@app/types/Auth';
import { JwtHelperService } from '@jjmhalew/angular-jwt';
import { Navigate } from '@ngxs/router-plugin';
import { STORAGE_ENGINE } from '@ngxs/storage-plugin';
import { Action, NgxsOnInit, Selector, SelectorOptions, State, StateContext, Store } from '@ngxs/store';
import { patch } from '@ngxs/store/operators';
import { DashGisUtil } from '@shared/dash-gis-util';
import { LoggerService } from '@shared/services/logger.service';
import { RestService } from '@shared/services/rest.service';
import { AuthConfig, OAuthService } from 'angular-oauth2-oidc';
import { asyncScheduler, defer, from, Observable, of, Subject, Subscription, timer } from 'rxjs';
import { catchError, concatMap, filter, mergeMap, switchMap, take, takeUntil, tap } from 'rxjs/operators';
import { AppConfigState } from '../../state/appConfig/app-config.state';
import { ThrowError } from '../../state/notification/notification.actions';
import { ResetPasswordService } from '../services/reset-password.service';
import {
    AcceptedForgotPasswordRequest,
    BeginConfirmAccount,
    BeginForceResetPassword,
    BeginInitialAuthentication,
    BeginLogout,
    BeginOAuthInitialAuthentication,
    BeginPasswordInitialAuthentication,
    BeginPasswordLogin,
    BeginTokenRefresh,
    CheckConfirmationKey,
    CheckConfirmationKeyFailed,
    CheckConfirmationKeySuccess,
    CheckResetKey,
    ConfigureOpenId,
    ConfirmAccountFailed,
    ConfirmAccountSuccess,
    DisclaimerSeen,
    EndForceResetPassword,
    EndInitialAuthentication,
    InitForgotPasswordRequest,
    InitOpenIdSignUp,
    LoadAuthConfig,
    LoggedOut,
    LoginFailed,
    LoginSuccessful,
    NavigateToRequestedRoute,
    OpenIdLoginSuccessful,
    PrepareToLogin,
    RedirectToLogin,
    RequestUserInfo,
    ResetForgottenPassword,
    ResetForgottenPasswordFailure,
    ResetForgottenPasswordSuccess,
    ResetPassword,
    ResetPasswordFailed,
    ResetPasswordSuccess,
    SetAuthConfigOptions,
    SetRequestedRoute,
    SetUserInfo,
    TokenRefreshFailed,
    TokenRefreshSuccessful,
    UpdatePasswordComplexityUI
} from './auth.actions';
import {
    AuthMethods,
    AuthModel,
    LoginPayload,
    LoginResponse,
    OpenIdStateModel,
    RequestedRoute,
    TokenRefreshResponse,
    UserInfoResponse
} from './auth.model';

@State<AuthModel>({
    name: 'auth',
    defaults: {
        loaded: false,
        activeMethod: 'none',
        methods: {},
        token: '',
        refreshToken: '',
        validated: false,
        user: {
            id: null,
            username: '',
            roles: []
        },
        needsDisclaimer: true,
        remember: false,
        authenticationInProgress: true,
        refreshInProgress: false,
        isLoggingOut: false,
        isStartingUp: true,
        isInitialVisit: true,
        requestedRoute: null,
        forgotPasswordRequestState: 'none',
        forcePasswordResetInProgress: false,
        excludedRoutes: [ '/auth/forgot', '/auth/reset', '/auth/confirm', '/auth/disclaimer', '/auth/callback' ],
        openId: {
            oidcConfig: {},
            buttonText: 'Login with OpenID',
            initialized: false
        }
    }
})
@Injectable()
@SelectorOptions({ injectContainerState: false })
export class AuthState implements NgxsOnInit {
    private passwordTokenRefreshSub: Subscription;
    private loggedOut$ = new Subject<void>();

    constructor(
        @Inject(APP_BASE_HREF) private baseHref: string,
        private logger: LoggerService,
        private reset: ResetPasswordService,
        private rest: RestService,
        private store: Store,
        private router: Router,
        private jwtService: JwtHelperService,
        private oauthService: OAuthService,
        @Inject(STORAGE_ENGINE) private storage: Storage
    ) {}

    ngxsOnInit(ctx: StateContext<AuthModel>) {
        const token = this.storage.getItem('auth.token') || '';
        const refreshToken = this.storage.getItem('auth.refreshToken') || '';
        ctx.setState(
            patch({
                token,
                refreshToken
            })
        );
        const appConfigLoaded$ = this.store.select(AppConfigState.loaded).pipe(filter(Boolean));
        appConfigLoaded$
            .pipe(
                tap(() => setTimeout(() => ctx.dispatch(new LoadAuthConfig()), 0)),
                take(1)
            )
            .subscribe();
    }

    @Action(LoadAuthConfig)
    loadAuthConfig(ctx: StateContext<AuthModel>) {
        return this.rest.apiGet('auth/config').pipe(
            tap((authConfig: AuthMethods) => {
                ctx.setState(
                    patch<AuthModel>({
                        methods: authConfig,
                        loaded: true
                    })
                );
            }),
            tap(() => ctx.dispatch(new BeginInitialAuthentication()))
        );
    }

    @Action(BeginInitialAuthentication)
    beginInitialAuthentication({ dispatch }: StateContext<AuthModel>) {
        return dispatch(new BeginOAuthInitialAuthentication()).pipe(
            concatMap(() => dispatch(new BeginPasswordInitialAuthentication())),
            concatMap(() => dispatch(new EndInitialAuthentication()))
        );
    }

    @Action(BeginOAuthInitialAuthentication)
    beginOpenIdInitialAuthentication({ getState, dispatch }: StateContext<AuthModel>) {
        // load the discovery document
        const state = getState();
        if (state.methods.hasOwnProperty('oidc')) {
            const oidcMethod = state.methods['oidc'];
            const oidcConfig = this.getOpenIdConnectConfig(oidcMethod.config);
            return dispatch(
                new ConfigureOpenId({
                    oidcConfig: oidcConfig,
                    buttonText: oidcMethod.config.buttonText,
                    signupButtonText: oidcMethod.config?.signupButtonText
                })
            ).pipe(
                // setup refresh - not using silent refresh from angular-oauth2-oidc because it involves iframes
                take(1),
                tap(() => {
                    timer(1000, 10000)
                        .pipe(
                            filter(() => getState().openId.initialized),
                            filter(() => !!this.oauthService.getRefreshToken()),
                            filter(() => {
                                const token = this.oauthService.getAccessToken();
                                // pass the filter if there is no access token
                                return token ? this.jwtService.isTokenExpired(token, 180) : true;
                            }),
                            takeUntil(this.loggedOut$),
                            concatMap(() => from(this.oauthService.refreshToken()))
                        )
                        .subscribe();
                })
            );
        }
    }

    @Action(BeginPasswordInitialAuthentication)
    beginPasswordInitialAuthentication({ getState, dispatch }: StateContext<AuthModel>) {
        const state = getState();

        // Not an authenticated route
        const currentPath = this.router.url.split(/[#?]/)[0];
        if (state.excludedRoutes.includes(currentPath) || (!state.refreshToken && !state.token)) {
            return of();
        }

        //  there's a refresh token
        if (state.refreshToken) {
            return dispatch(new BeginTokenRefresh());
        }
    }

    @Action(EndInitialAuthentication)
    endInitialAuthentication({ setState }: StateContext<AuthModel>) {
        setState(
            patch({
                authenticationInProgress: false,
                isStartingUp: false
            })
        );
    }

    @Action(SetAuthConfigOptions)
    setAuthConfigOptions({ setState }: StateContext<AuthModel>, action: SetAuthConfigOptions) {
        // if the disclaimer is not enabled, we can never need to display it
        if (!action.payload.enableDisclaimer) {
            setState(patch({ needsDisclaimer: false }));
        }
    }

    @Action(RequestUserInfo)
    requestUserInfo({ getState, dispatch }: StateContext<AuthModel>) {
        const state = getState();
        const token = this.oauthService.getIdToken() || state.token;
        if (token) {
            return this.rest
                .apiPost<UserInfoResponse>('auth/userinfo', { token })
                .pipe(tap((response) => asyncScheduler.schedule(() => dispatch(new SetUserInfo(response)))));
        }
    }

    @Action(SetUserInfo)
    setUserInfo({ setState, dispatch }: StateContext<AuthModel>, action: SetUserInfo) {
        if (action.payload?.user) {
            setState(
                patch({
                    user: patch({
                        id: action.payload.user.id,
                        username: action.payload.user.username,
                        authenticator: action.payload.user.authenticator,
                        roles: action.payload.user.roles
                    }),
                    validated: true,
                    isInitialVisit: false,
                    authenticationInProgress: false
                })
            );
            return dispatch(new NavigateToRequestedRoute());
        } else {
            setState(
                patch({
                    user: patch({
                        id: -1,
                        username: '',
                        roles: [] as string[]
                    }),
                    validated: false,
                    authenticationInProgress: false
                })
            );
        }
    }

    @Action(BeginTokenRefresh)
    beginTokenRefresh({ getState, setState }: StateContext<AuthModel>) {
        const state = getState();
        if (state.refreshInProgress) {
            return of();
        }
        setState(patch({ refreshInProgress: true }));
        const opts = { refreshToken: state.refreshToken };
        return this.rest
            .apiPost<TokenRefreshResponse>('auth/refresh', opts, {}, { skipErrorHandler: true })
            .pipe(
                concatMap((response) =>
                    this.store.dispatch(
                        response.success
                            ? new TokenRefreshSuccessful(response)
                            : new TokenRefreshFailed(response.message)
                    )
                )
            );
    }

    @Action(TokenRefreshSuccessful)
    tokenRefreshSuccessful({ dispatch, setState }: StateContext<AuthModel>, action: TokenRefreshSuccessful) {
        setState(
            patch<AuthModel>({
                token: action.payload.token,
                refreshToken: action.payload.refreshToken,
                validated: true,
                activeMethod: 'password',
                authenticationInProgress: false,
                refreshInProgress: false
            })
        );
        this.storage.setItem('auth.token', action.payload.token);
        this.storage.setItem('auth.refreshToken', action.payload.refreshToken);
        this.initPasswordAutoTokenRefresh();
        return dispatch(new RequestUserInfo());
    }

    @Action(TokenRefreshFailed)
    tokenRefreshFailed({ getState, setState, dispatch }: StateContext<AuthModel>, action: TokenRefreshFailed) {
        this.logger.warn(`Token refresh failed. Reason: ${ action.reason }`);
        setState(
            patch({
                token: '',
                refreshToken: '',
                validated: false,
                authenticationInProgress: false,
                refreshInProgress: false
            })
        );
        this.storage.removeItem('auth.token');
        this.storage.removeItem('auth.refreshToken');

        return dispatch(new RedirectToLogin(getState().requestedRoute));
    }

    @Action(PrepareToLogin)
    prepareToLogin({ setState }: StateContext<AuthModel>, action: PrepareToLogin) {
        // in order for the 'split' storage engine proxy to work, we have to set the value of
        // 'remember' directly in storage before changing the state.
        // We are NOT saving it via the storage plugin

        // we also need to make sure we remove it after setting the state in the logout action
        this.storage.setItem('auth.remember', action.payload.remember.toString());
        setState(
            patch({
                authenticationInProgress: true,
                activeMethod: action.payload.method,
                remember: action.payload.remember
            })
        );
    }

    @Action(BeginPasswordLogin)
    beginPasswordLogin({ dispatch }: StateContext<AuthModel>, action: BeginPasswordLogin) {
        const loginParams = {
            username: action.payload.credentials.username,
            password: action.payload.credentials.password
        };
        return this.rest.apiPost<LoginResponse>('auth/login', loginParams).pipe(
            catchError((err) => {
                this.logger.error(err);
                return of<LoginResponse>({
                    user: {
                        id: -1,
                        username: '',
                        roles: [] as string[]
                    },
                    token: null,
                    refreshToken: null
                });
            }),
            tap((result: LoginResponse) => {
                if (result.success) {
                    this.initPasswordAutoTokenRefresh();
                }
            }),
            concatMap((result: LoginResponse) => {
                if (result.success) {
                    this.storage.setItem('auth.token', result.token);
                    this.storage.setItem('auth.refreshToken', result.refreshToken);
                    return dispatch(
                        new LoginSuccessful({
                            user: result.user,
                            token: result.token,
                            refreshToken: result.refreshToken
                        })
                    );
                } else {
                    return dispatch(new LoginFailed({ message: 'Login failed' }));
                }
            })
        );
    }

    @Action(LoginSuccessful)
    loginSuccessful({ dispatch, getState, setState }: StateContext<AuthModel>, action: LoginSuccessful) {
        const stateChanges = {
            authenticationInProgress: false,
            isStartingUp: false,
            validated: true
        };
        setState(
            patch({
                ...stateChanges,
                ...action.payload
            })
        );
        const state = getState();
        return state.requestedRoute ? dispatch(new NavigateToRequestedRoute()) : dispatch(new Navigate([ '/' ]));
    }

    @Action(LoginFailed)
    loginFailed({ setState }: StateContext<AuthModel>) {
        setState(
            patch({
                authenticationInProgress: false,
                isStartingUp: false
            })
        );
    }

    @Action(BeginLogout)
    beginLogout({ getState, setState, dispatch }: StateContext<AuthModel>, action: BeginLogout) {
        const emptyRoles: string[] = [];
        const state = getState();
        const logout$ =
            state.activeMethod === 'oidc'
                ? defer(() => {
                    this.oauthService.logOut(true);
                    return of(void 0);
                })
                : defer(() => this.rest.apiPost<void>('auth/logout', { refreshToken: state.refreshToken }));
        return logout$.pipe(
            tap(() => this.loggedOut$.next()),
            tap(() =>
                setState(
                    patch({
                        authenticationInProgress: true,
                        isLoggingOut: true,
                        token: '',
                        refreshToken: '',
                        validated: false,
                        activeMethod: 'none',
                        user: patch({
                            id: null,
                            username: '',
                            authenticator: null,
                            roles: emptyRoles
                        })
                    })
                )
            ),
            tap(() => {
                this.storage.removeItem('auth.token');
                this.storage.removeItem('auth.refreshToken');
                this.storage.removeItem('auth.remember');
            }),
            concatMap(() => defer(() => dispatch(new LoggedOut(action.redirect))))
        );
    }

    @Action(LoggedOut)
    loggedOut({ dispatch, setState }: StateContext<AuthModel>, action: LoggedOut) {
        setState(
            patch({
                authenticationInProgress: false,
                isStartingUp: false
            })
        );
        return action.redirect ? dispatch(new RedirectToLogin({ url: '/' })) : true;
    }

    @Action(SetRequestedRoute)
    setRequestedRoute({ setState }: StateContext<AuthModel>, action: SetRequestedRoute) {
        setState(patch({ requestedRoute: action.requestedRoute }));
    }

    @Action(RedirectToLogin)
    redirectToLogin({ dispatch, setState }: StateContext<AuthModel>, action: RedirectToLogin) {
        setState(patch({ authenticationInProgress: false }));
        if (this.shouldOverwriteRequestedRoute(action.route)) {
            setState(
                patch({
                    requestedRoute: action.route
                })
            );
        }
        setTimeout(() => dispatch(new Navigate([ '/auth/login' ], null, { queryParamsHandling: 'preserve' })), 0);
    }

    @Action(NavigateToRequestedRoute)
    navigateToRequestedRoute(
        { dispatch, getState, setState }: StateContext<AuthModel>,
        action: NavigateToRequestedRoute
    ) {
        const requestedRoute = getState().requestedRoute || action.fallbackRoute;
        if (requestedRoute) {
            setState(patch({ requestedRoute: null }));
            setTimeout(() => dispatch(new Navigate([ requestedRoute.url ], requestedRoute.queryParams)), 0);
        }
    }

    @Action(DisclaimerSeen)
    disclaimerSeen({ dispatch, getState, setState }: StateContext<AuthModel>) {
        const state = getState();
        const autoLogin = this.store.selectSnapshot(AppConfigState.autoLogin);
        const isInitialVisit = state.isInitialVisit;
        const isNoActiveMethod = state.activeMethod === 'none';
        const fullPath = this.router.url.split(/[?#]/)[0];
        // disclaimer is not authenticated, but we don't want to force logout either
        const excludedRoutes = state.excludedRoutes.filter((r) => r !== '/auth/disclaimer');
        const skipAuthByRoute = excludedRoutes.includes(fullPath);
        setState(
            patch({
                needsDisclaimer: false,
                isInitialVisit: false
            })
        );
        if (skipAuthByRoute) {
            setTimeout(() => dispatch(new BeginLogout(false)), 0);
            return;
        }
        if (isInitialVisit && isNoActiveMethod && autoLogin.enabled && autoLogin.guestuser && autoLogin.guestpassword) {
            const payload: LoginPayload = {
                credentials: {
                    username: autoLogin.guestuser,
                    password: autoLogin.guestpassword
                }
            };
            setTimeout(
                () =>
                    dispatch(
                        new PrepareToLogin({
                            method: 'password',
                            remember: false
                        })
                    ),
                0
            );
            setTimeout(() => dispatch(new BeginPasswordLogin(payload)), 0);
        } else {
            setTimeout(() => dispatch(new NavigateToRequestedRoute({ url: '/' })), 0);
        }
    }

    @Action(InitForgotPasswordRequest)
    initForgotPasswordRequest({ dispatch, setState }: StateContext<AuthModel>, action: InitForgotPasswordRequest) {
        setState(patch({ forgotPasswordRequestState: 'started' }));
        const data = { identifier: action.identifier };
        return this.rest.apiPost('auth/password/forgot', data, null, { skipErrorHandler: false }).pipe(
            concatMap(() => dispatch(new AcceptedForgotPasswordRequest())),
            catchError((err) => dispatch(new ThrowError(err)))
        );
    }

    @Action(AcceptedForgotPasswordRequest)
    acceptedForgotPasswordRequest({ setState }: StateContext<AuthModel>) {
        setState(patch({ forgotPasswordRequestState: 'complete' }));
    }

    @Action(CheckResetKey)
    checkResetKey({ dispatch }: StateContext<AuthModel>, action: CheckResetKey) {
        return this.reset.verifyResetKey$(action.resetKey).pipe(
            mergeMap((result) => {
                const params = {
                    key: action.resetKey,
                    valid: !(result === false)
                };
                return dispatch(new Navigate([ '/auth/reset' ], params));
            })
        );
    }

    @Action(ResetForgottenPassword)
    resetForgottenPassword({ dispatch }: StateContext<AuthModel>, action: ResetForgottenPassword) {
        return this.rest
            .apiPost<LoginResponse>('auth/password/forgot/reset', action.payload, {}, { skipErrorHandler: true })
            .pipe(
                take(1),
                tap((result) => setTimeout(() => dispatch(new LoginSuccessful(result)), 0)),
                tap(() => setTimeout(() => dispatch(new ResetForgottenPasswordSuccess()), 50)),
                catchError((err) => dispatch(new ResetForgottenPasswordFailure(err.message)))
            );
    }

    @Action(ResetPassword)
    resetPassword({ dispatch }: StateContext<AuthModel>, action: ResetPassword) {
        return this.reset.resetPassword$(action.payload).pipe(
            take(1),
            switchMap((result) =>
                result.success
                    ? dispatch([ new LoginSuccessful(result), new ResetPasswordSuccess() ])
                    : dispatch(new ResetPasswordFailed(result.message))
            )
        );
    }

    @Action(ResetPasswordSuccess)
    resetPasswordSuccess() {}

    @Action(ResetPasswordFailed)
    resetPasswordFailed() {}

    @Action(UpdatePasswordComplexityUI)
    updatePasswordComplexityUI() {}

    @Action(CheckConfirmationKey)
    checkConfirmationKey({ dispatch }: StateContext<AuthModel>, action: CheckConfirmationKey) {
        return this.reset.verifyConfirmationKey$(action.confirmationKey).pipe(
            // mergeMap((result) => {
            //     const params = { key: action.confirmationKey, valid: !(result === false) };
            //     return dispatch(new Navigate([ '/auth/confirm' ], params));
            // })
            tap((result) =>
                result
                    ? setTimeout(() => dispatch(new CheckConfirmationKeySuccess()), 0)
                    : setTimeout(() => dispatch(new CheckConfirmationKeyFailed()), 0)
            )
        );
    }

    @Action(CheckConfirmationKeyFailed)
    checkConfirmationKeyFailed({ setState }: StateContext<AuthModel>) {
        setState(patch({ authenticationInProgress: false }));
    }

    @Action(BeginConfirmAccount)
    beginConfirmAccount({ dispatch }: StateContext<AuthModel>, action: BeginConfirmAccount) {
        setTimeout(
            () =>
                dispatch(
                    new PrepareToLogin({
                        method: 'password',
                        remember: false
                    })
                ),
            0
        );
        return this.reset.confirmAccount$(action.payload).pipe(
            take(1),
            switchMap((result) =>
                result.success
                    ? dispatch([ new LoginSuccessful(result), new ConfirmAccountSuccess() ])
                    : dispatch(new ConfirmAccountFailed(result.message))
            )
        );
    }

    @Action(BeginForceResetPassword)
    beginForceResetPassword({ setState }: StateContext<AuthModel>) {
        setState(patch({ forcePasswordResetInProgress: true }));
    }

    @Action(EndForceResetPassword)
    endForceResetPassword({ setState }: StateContext<AuthModel>) {
        setState(patch({ forcePasswordResetInProgress: false }));
    }

    @Action(OpenIdLoginSuccessful)
    public openIdLoginSuccessful({ dispatch }: StateContext<AuthModel>) {
        let stateUrl = this.oauthService.state;
        let setRoute$: Observable<any> = of(null);
        if (!stateUrl.startsWith('/')) {
            stateUrl = decodeURIComponent(stateUrl);
            const requestedRoute = DashGisUtil.buildRequestedRoute(stateUrl);
            setRoute$ = dispatch(new SetRequestedRoute(requestedRoute));
        }
        return setRoute$.pipe(
            take(1),
            concatMap(() =>
                dispatch(new RequestUserInfo()).pipe(
                    concatMap(() =>
                        dispatch(
                            new LoginSuccessful({
                                activeMethod: 'oidc',
                                token: '',
                                refreshToken: ''
                            })
                        )
                    )
                )
            )
        );
    }

    @Action(ConfigureOpenId)
    public configureOpenId({ dispatch, setState }: StateContext<AuthModel>, action: ConfigureOpenId) {
        this.oauthService.configure(action.config.oidcConfig);
        return from(this.oauthService.loadDiscoveryDocumentAndTryLogin()).pipe(
            catchError((err) => {
                this.logger.error('Error in OpenId process', err);
                return this.store.dispatch(new RedirectToLogin(null));
            }),
            concatMap(() => {
                if (this.oauthService.hasValidIdToken()) {
                    return dispatch(new OpenIdLoginSuccessful());
                }
                return of(null);
            }),
            tap(() =>
                setState(
                    patch({
                        openId: {
                            oidcConfig: action.config.oidcConfig,
                            buttonText: action.config.buttonText,
                            signupButtonText: action.config?.signupButtonText,
                            initialized: true
                        }
                    })
                )
            )
        );
    }

    @Action(InitOpenIdSignUp)
    initOpenIdSignUp({ getState }: StateContext<AuthModel>) {
        const state = getState();
        const oidcConfig = this.getOpenIdConnectConfig(state.openId, true);
        this.oauthService.configure(oidcConfig);
        return this.oauthService.loadDiscoveryDocumentAndLogin();
    }

    private shouldOverwriteRequestedRoute(newRequestedRoute: RequestedRoute) {
        const url = newRequestedRoute?.url || null;
        const q = newRequestedRoute?.queryParams;

        const ignoreNewUrl = [ null, undefined, '' ].includes(url);

        const ignoreParams = q ? Object.keys(q).filter((p) => [ 'code', 'state', 'error' ].includes(p)).length > 0 : true;

        return !(ignoreNewUrl || (ignoreParams && url === '/'));
    }

    private getOpenIdConnectConfig(config: Partial<OpenIdStateModel>, isSignUp = false): AuthConfig {
        const baseUri = new URL(`${ window.location.origin }${ this.baseHref }/`).toString();
        return {
            issuer: config.oidcConfig.issuer,
            clientId: config.oidcConfig.clientId,
            customQueryParams: isSignUp ? { prompt: 'create' } : null,
            redirectUri: baseUri,
            postLogoutRedirectUri: baseUri,
            responseType: 'code',
            requireHttps: 'remoteOnly',

            // Ask offline_access to support refresh token refreshes
            scope: config.oidcConfig.scope || 'openid profile email offline_access',

            // Needed for Code Flow to suggest using iframe-based refreshes
            useSilentRefresh: false,

            strictDiscoveryDocumentValidation: config.oidcConfig.strictDiscoveryDocumentValidation ?? true,
            logoutUrl: config.oidcConfig.logoutUrl || null,
            // For faster testing
            timeoutFactor: 1.0,
            sessionChecksEnabled: false,
            sessionCheckIntervall: 10000,
            // Also requires enabling "Verbose" level in devtools
            showDebugInformation: config.oidcConfig.showDebugInformation || false,
            // https://github.com/manfredsteyer/angular-oauth2-oidc/issues/457#issuecomment-431807040
            clearHashAfterLogin: false,
            // Real semicolon gets mangled by IdentityServer's URI encoding
            nonceStateSeparator: ';'
        };
    }

    private initPasswordAutoTokenRefresh() {
        if (!this.passwordTokenRefreshSub || this.passwordTokenRefreshSub.closed) {
            this.passwordTokenRefreshSub = timer(1000, 10000)
                .pipe(
                    filter(() => !!this.storage.getItem('auth.refreshToken')),
                    filter(() => {
                        const accessToken = this.storage.getItem('auth.token');
                        return accessToken ? this.jwtService.isTokenExpired(accessToken, 90) : true;
                    }),
                    takeUntil(this.loggedOut$),
                    switchMap(() => this.store.dispatch(new BeginTokenRefresh()))
                )
                .subscribe();
        }
    }

    @Selector([ AuthState ])
    static loaded(state: AuthModel): boolean {
        return state.loaded;
    }

    @Selector([ AuthState ])
    static authMethods(state: AuthModel): string[] {
        return Object.keys(state.methods);
    }

    @Selector([ AuthState ])
    static methodConfig(state: AuthModel): (method: string) => any {
        return (method: string) => state.methods[method].config;
    }

    @Selector([ AuthState ])
    static loginMethods(state: AuthModel): string[] {
        const arr = Object.keys(state.methods).map((k) => state.methods[k].type);
        // make unique
        return [ ...new Set(arr) ];
    }

    @Selector([ AuthState ])
    static token(state: AuthModel) {
        return state.token;
    }

    @Selector([ AuthState ])
    static validated(state: AuthModel) {
        return state.validated;
    }

    @Selector([ AuthState ])
    static refreshToken(state: AuthModel) {
        return state.refreshToken;
    }

    @Selector([ AuthState ])
    static remember(state: AuthModel) {
        return state.remember;
    }

    @Selector([ AuthState ])
    static user(state: AuthModel) {
        return state.user;
    }

    @Selector([ AuthState, AppConfigState.enableDisclaimer ])
    static needsDisclaimer(state: AuthModel, enableDisclaimer: boolean) {
        return state.needsDisclaimer && enableDisclaimer;
    }

    @Selector([ AuthState ])
    static authenticationInProgress(state: AuthModel) {
        return state.authenticationInProgress;
    }

    @Selector([ AuthState ])
    static isStartingUp(state: AuthModel) {
        return state.isStartingUp;
    }

    @Selector([ AuthState ])
    static requestedRoute(state: AuthModel) {
        return state.requestedRoute;
    }

    @Selector([ AuthState ])
    static activeMethod(state: AuthModel) {
        return state.activeMethod;
    }

    @Selector([ AuthState ])
    static forgotPasswordRequestState(state: AuthModel) {
        return state.forgotPasswordRequestState;
    }

    @Selector([ AuthState ])
    static forcePasswordResetInProgress(state: AuthModel) {
        return state.forcePasswordResetInProgress;
    }

    @Selector([ AuthState ])
    static excludedRoutes(state: AuthModel) {
        return state.excludedRoutes;
    }

    @Selector([ AuthState, AppConfigState.autoLogin ])
    static isDefaultUser(state: AuthModel, autoLogin: AutoLoginConfig) {
        return autoLogin && state.user.username === autoLogin.guestuser;
    }

    @Selector([ AuthState ])
    static shouldInitOpenIdConnect(state: AuthModel): boolean {
        return state.methods.hasOwnProperty('oidc');
    }

    @Selector([ AuthState ])
    static openIdInitialized(state: AuthModel) {
        return state.openId.initialized;
    }

    @Selector([ AuthState ])
    static openIdButtonText(state: AuthModel) {
        return state.openId.buttonText;
    }

    @Selector([ AuthState ])
    static openIdSignupButtonText(state: AuthModel) {
        return state.openId?.signupButtonText;
    }
}
